import { AUTH_FOR } from 'src/types/oAuth';
import { RUNDOWN_UPDATE_TYPE } from 'src/types/rundownUpdate';
import { UNIFIED_DIGEST_ACTIVE_JOB_ROUTE_SEGMENT } from 'src/constants/unifiedDigest';
import {
  MEMORY_BATCH_TYPES,
  MEMORY_STATUS,
  MEMORY_LEVELS,
  MEMORY_BATCH_INGESTION_STATUS,
} from './memory';
import { SENT_PROMPT_FEEDBACK_FILTER } from './sentPrompts';
import { BrainMemoryCountLevel } from 'src/types/brainMemory';
import { UnifiedDigestRecapMode } from 'src/types/unifiedDigest';

export const BACKEND_BASE_URL = process.env.REACT_APP_HANA_BACKEND_URL;

const UNIFIED_DIGEST_BASE_ROUTE = '/unified-digest';
const SENT_PROMPTS_ROUTE = '/sent-prompts';
const UNIFIED_DIGEST_WHATS_HAPPENING_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/whats-happening`;
const UNIFIED_DIGEST_WHATS_HAPPENING_SEARCH_ROUTE = `${UNIFIED_DIGEST_WHATS_HAPPENING_ROUTE}/search`;
const UNIFIED_DIGEST_WHATS_HAPPENING_JOBS_ROUTE = `${UNIFIED_DIGEST_WHATS_HAPPENING_ROUTE}/jobs`;
const UNIFIED_DIGEST_WHATS_HAPPENING_ACTIVE_JOB_ROUTE = `${UNIFIED_DIGEST_WHATS_HAPPENING_JOBS_ROUTE}/${UNIFIED_DIGEST_ACTIVE_JOB_ROUTE_SEGMENT}`;
const UNIFIED_DIGEST_GENERATE_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/generate`;
const UNIFIED_DIGEST_SEARCH_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/search`;
const UNIFIED_DIGEST_EMAIL_PREFERENCES_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/email-preferences`;
const PERSONAL_ACTION_RECAP_PREVIEW_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/action-recap/preview`;
const UNIFIED_DIGEST_SUPER_ADMIN_EMAIL_PREFERENCES_ROUTE = `${UNIFIED_DIGEST_BASE_ROUTE}/super-admin/email-preferences`;
const SPRINT_BASE_ROUTE = '/sprint';
const MEETING_OVERVIEW_ROUTE = '/meeting-overview';
const SENT_PROMPTS_SUMMARY_ROUTE = `${SENT_PROMPTS_ROUTE}/summary`;

const appendUnifiedDigestScopeParams = (
  params: URLSearchParams,
  chatScope?: {
    mode: string;
    spaceIds: string[];
    spaceNames?: string[];
  } | null,
) => {
  if (!chatScope?.mode || !Array.isArray(chatScope.spaceIds)) {
    return;
  }

  const trimmedSpaceIds = chatScope.spaceIds
    .map((spaceId) => spaceId.trim())
    .filter((spaceId) => spaceId.length > 0);

  if (trimmedSpaceIds.length === 0) {
    return;
  }

  params.set('scopeMode', chatScope.mode);

  trimmedSpaceIds.forEach((spaceId) => {
    params.append('spaceIds', spaceId);
  });

  (chatScope.spaceNames ?? [])
    .map((spaceName) => spaceName.trim())
    .filter((spaceName) => spaceName.length > 0)
    .forEach((spaceName) => {
      params.append('spaceNames', spaceName);
    });
};

export const API_ROUTES_FOR_AXIOS = {
  verifyDriveFileAccess: () => `/oauth/drive-file-access`,
  getSessionDetails: () => `/dashboard/check-valid-session`,
  getUserInfoByCookie: () => `/users/get-user-info`,
  logout: () => `/dashboard/logout`,
  groupNameAvailabilityCheck: () =>
    `/report-group/organizations/name-availability-check`,
  createReportGroup: () => `/report-group/organizations`,
  generateReportGroupSummary: () => `/report-group/organizations/summary`,
  createReportGroupSummaryJob: () => `/report-group/organizations/summary/jobs`,
  getReportGroupSummaryJobStatus: (processingId: string) =>
    `/report-group/organizations/summary/jobs/${processingId}`,
  getReportGroupDetailsById: (groupId: string) => `/report-group/${groupId}`,
  deleteReportGroupById: (groupId: string) => `/report-group/${groupId}`,
  getReportGroupsByOrgId: (page: number, limit: number) =>
    `/report-group/organizations?page=${page}&limit=${limit}`,
  getSentPrompts: (
    page: number,
    limit: number,
    startTimeStamp?: string | null,
    endTimeStamp?: string | null,
    organizationId?: string,
    userId?: string,
    excludedOrganizationIds?: string[],
    userFeedbackFilter: SENT_PROMPT_FEEDBACK_FILTER = SENT_PROMPT_FEEDBACK_FILTER.ALL,
    corporateOnly = false,
  ) => {
    const params = new URLSearchParams();

    params.set('page', String(page));
    params.set('limit', String(limit));

    if (startTimeStamp) {
      params.set('startTimeStamp', startTimeStamp);
    }

    if (endTimeStamp) {
      params.set('endTimeStamp', endTimeStamp);
    }

    if (organizationId) {
      params.set('organizationId', organizationId);
    }

    if (userId) {
      params.set('userId', userId);
    }

    if (excludedOrganizationIds && excludedOrganizationIds.length > 0) {
      excludedOrganizationIds.forEach((value) => {
        const trimmedValue = value.trim();

        if (trimmedValue.length > 0) {
          params.append('excludedOrganizationIds', trimmedValue);
        }
      });
    }

    params.set('userFeedbackFilter', String(userFeedbackFilter));
    params.set('corporateOnly', String(corporateOnly));

    const queryString = params.toString();

    return `${SENT_PROMPTS_ROUTE}?${queryString}`;
  },
  getSentPromptsSummary: (
    startTimeStamp?: string | null,
    endTimeStamp?: string | null,
    organizationId?: string,
    userId?: string,
    excludedOrganizationIds?: string[],
    userFeedbackFilter: SENT_PROMPT_FEEDBACK_FILTER = SENT_PROMPT_FEEDBACK_FILTER.ALL,
    corporateOnly = false,
  ) => {
    const params = new URLSearchParams();

    if (startTimeStamp) {
      params.set('startTimeStamp', startTimeStamp);
    }

    if (endTimeStamp) {
      params.set('endTimeStamp', endTimeStamp);
    }

    if (organizationId) {
      params.set('organizationId', organizationId);
    }

    if (userId) {
      params.set('userId', userId);
    }

    if (excludedOrganizationIds && excludedOrganizationIds.length > 0) {
      excludedOrganizationIds.forEach((value) => {
        const trimmedValue = value.trim();

        if (trimmedValue.length > 0) {
          params.append('excludedOrganizationIds', trimmedValue);
        }
      });
    }

    params.set('userFeedbackFilter', String(userFeedbackFilter));
    params.set('corporateOnly', String(corporateOnly));

    const queryString = params.toString();

    return queryString
      ? `${SENT_PROMPTS_SUMMARY_ROUTE}?${queryString}`
      : SENT_PROMPTS_SUMMARY_ROUTE;
  },
  getInvocationHistory: (page: number, limit: number) =>
    `/invocation-history?page=${page}&limit=${limit}`,
  getActivity: (params: URLSearchParams) => `/v1/activity?${params.toString()}`,
  getActivityGaps: (params: URLSearchParams) =>
    `/v1/activity/gaps?${params.toString()}`,
  getActivityGapAttempts: (gapId: string) =>
    `/v1/activity/gaps/${encodeURIComponent(gapId)}/attempts`,
  applyActivityGapCommand: (gapId: string) =>
    `/v1/activity/gaps/${encodeURIComponent(gapId)}/commands`,
  getActivityAuditEvents: (params: URLSearchParams) =>
    `/v1/admin/audit-events?${params.toString()}`,
  getOperationalFailures: (params: URLSearchParams) =>
    `/v1/admin/operational-failures?${params.toString()}`,
  decideOperationalFailure: (failureId: string) =>
    `/v1/admin/operational-failures/${encodeURIComponent(failureId)}/decision`,
  getInvocationHistoryForSuperAdmin: (
    page: number,
    limit: number,
    orgId?: string,
    userId?: string,
  ) =>
    `/invocation-history/super-admin?page=${page}&limit=${limit}${
      orgId ? `&orgId=${orgId}` : ''
    }${userId ? `&userId=${userId}` : ''}`,
  searchInvocationHistory: (page: number, limit: number, search: string) =>
    `/invocation-history/search?page=${page}&limit=${limit}&search=${encodeURIComponent(
      search,
    )}`,
  searchInvocationHistoryForSuperAdmin: (
    page: number,
    limit: number,
    search: string,
    orgId?: string,
    userId?: string,
  ) =>
    `/invocation-history/super-admin/search?page=${page}&limit=${limit}&search=${encodeURIComponent(
      search,
    )}${orgId ? `&orgId=${orgId}` : ''}${userId ? `&userId=${userId}` : ''}`,
  getUnifiedDigestWhatsHappening: (
    timeframe?: string,
    referenceDate?: string,
    chatScope?: {
      mode: string;
      spaceIds: string[];
      spaceNames?: string[];
    } | null,
    recapMode?: UnifiedDigestRecapMode,
  ) => {
    const params = new URLSearchParams();

    if (timeframe) {
      params.set('timeframe', timeframe);
    }

    if (referenceDate) {
      params.set('referenceDate', referenceDate);
    }

    appendUnifiedDigestScopeParams(params, chatScope);

    if (recapMode) params.set('recapMode', recapMode);

    const queryString = params.toString();

    return queryString
      ? `${UNIFIED_DIGEST_WHATS_HAPPENING_ROUTE}?${queryString}`
      : UNIFIED_DIGEST_WHATS_HAPPENING_ROUTE;
  },
  createUnifiedDigestWhatsHappeningJob: () =>
    UNIFIED_DIGEST_WHATS_HAPPENING_JOBS_ROUTE,
  getUnifiedDigestWhatsHappeningJobStatus: (processingId: string) =>
    `${UNIFIED_DIGEST_WHATS_HAPPENING_JOBS_ROUTE}/${processingId}`,
  getUnifiedDigestWhatsHappeningActiveJob: (
    timeframe?: string,
    chatScope?: {
      mode: string;
      spaceIds: string[];
      spaceNames?: string[];
    } | null,
    recapMode?: UnifiedDigestRecapMode,
  ) => {
    const params = new URLSearchParams();

    if (timeframe) {
      params.set('timeframe', timeframe);
    }

    appendUnifiedDigestScopeParams(params, chatScope);

    if (recapMode) params.set('recapMode', recapMode);

    const queryString = params.toString();

    return queryString
      ? `${UNIFIED_DIGEST_WHATS_HAPPENING_ACTIVE_JOB_ROUTE}?${queryString}`
      : UNIFIED_DIGEST_WHATS_HAPPENING_ACTIVE_JOB_ROUTE;
  },
  getMeetingOverview: (
    startDate?: string,
    endDate?: string,
    pageToken?: string,
    pageSize?: number,
  ) => {
    const params = new URLSearchParams();

    if (startDate) {
      params.set('startDate', startDate);
    }

    if (endDate) {
      params.set('endDate', endDate);
    }

    if (pageToken) {
      params.set('pageToken', pageToken);
    }

    if (pageSize) {
      params.set('pageSize', String(pageSize));
    }

    const queryString = params.toString();

    return queryString
      ? `${MEETING_OVERVIEW_ROUTE}?${queryString}`
      : MEETING_OVERVIEW_ROUTE;
  },
  getMeetingOverviewDetails: (params: {
    conferenceRecordId: string;
    meetingCode?: string | null;
    meetingTitle?: string | null;
    meetingLink?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    windowStart?: string | null;
    windowEnd?: string | null;
  }) => {
    const queryParams = new URLSearchParams();

    queryParams.set('conferenceRecordId', params.conferenceRecordId);

    if (params.meetingCode) {
      queryParams.set('meetingCode', params.meetingCode);
    }

    if (params.meetingTitle) {
      queryParams.set('meetingTitle', params.meetingTitle);
    }

    if (params.meetingLink) {
      queryParams.set('meetingLink', params.meetingLink);
    }

    if (params.startTime) {
      queryParams.set('startTime', params.startTime);
    }

    if (params.endTime) {
      queryParams.set('endTime', params.endTime);
    }

    if (params.windowStart) {
      queryParams.set('windowStart', params.windowStart);
    }

    if (params.windowEnd) {
      queryParams.set('windowEnd', params.windowEnd);
    }

    return `${MEETING_OVERVIEW_ROUTE}/details?${queryParams.toString()}`;
  },
  ingestMeetingTranscript: () => `${MEETING_OVERVIEW_ROUTE}/ingest`,
  startMeetingAutoTracking: () => `${MEETING_OVERVIEW_ROUTE}/tracking`,
  getGoogleTasksSourceOverview: () => '/google-tasks-source/overview',
  connectGoogleTasksSource: () => '/google-tasks-source/connect',
  syncGoogleTasksSource: (batchId: string) =>
    `/google-tasks-source/sync/${encodeURIComponent(batchId)}`,
  searchUnifiedDigestWhatsHappening: (
    query: string,
    timeframe?: string,
    limit?: number,
    offset?: number,
    includeRawSources?: boolean,
    chatScope?: {
      mode: string;
      spaceIds: string[];
      spaceNames?: string[];
    } | null,
    recapMode?: UnifiedDigestRecapMode,
  ) => {
    const params = new URLSearchParams();

    params.set('q', query);

    if (timeframe) {
      params.set('timeframe', timeframe);
    }

    if (typeof limit === 'number') {
      params.set('limit', String(limit));
    }

    if (typeof offset === 'number') {
      params.set('offset', String(offset));
    }

    if (typeof includeRawSources === 'boolean') {
      params.set('includeRawSources', String(includeRawSources));
    }

    appendUnifiedDigestScopeParams(params, chatScope);

    if (recapMode) params.set('recapMode', recapMode);

    return `${UNIFIED_DIGEST_WHATS_HAPPENING_SEARCH_ROUTE}?${params.toString()}`;
  },
  generateUnifiedDigest: () => UNIFIED_DIGEST_GENERATE_ROUTE,
  listUnifiedDigests: (params?: {
    recapMode?: UnifiedDigestRecapMode;
    timeframe?: string;
    chatScope?: {
      mode: string;
      spaceIds: string[];
      spaceNames?: string[];
    } | null;
    limit?: number;
    before?: string;
    beforeId?: string;
  }) => {
    const paramsBuilder = new URLSearchParams();

    if (params?.timeframe) {
      paramsBuilder.set('timeframe', params.timeframe);
    }

    appendUnifiedDigestScopeParams(paramsBuilder, params?.chatScope);

    if (params?.recapMode) paramsBuilder.set('recapMode', params.recapMode);

    if (typeof params?.limit === 'number') {
      paramsBuilder.set('limit', String(params.limit));
    }

    if (params?.before) {
      paramsBuilder.set('before', params.before);
    }

    if (params?.beforeId) {
      paramsBuilder.set('beforeId', params.beforeId);
    }

    const queryString = paramsBuilder.toString();

    return queryString
      ? `${UNIFIED_DIGEST_BASE_ROUTE}?${queryString}`
      : UNIFIED_DIGEST_BASE_ROUTE;
  },
  searchUnifiedDigests: (params: {
    recapMode?: UnifiedDigestRecapMode;
    query: string;
    timeframe?: string;
    chatScope?: {
      mode: string;
      spaceIds: string[];
      spaceNames?: string[];
    } | null;
    limit?: number;
    offset?: number;
    includeRawSources?: boolean;
  }) => {
    const paramsBuilder = new URLSearchParams();

    paramsBuilder.set('q', params.query);

    if (params.timeframe) {
      paramsBuilder.set('timeframe', params.timeframe);
    }

    appendUnifiedDigestScopeParams(paramsBuilder, params.chatScope);

    if (params.recapMode) paramsBuilder.set('recapMode', params.recapMode);

    if (typeof params.limit === 'number') {
      paramsBuilder.set('limit', String(params.limit));
    }

    if (typeof params.offset === 'number') {
      paramsBuilder.set('offset', String(params.offset));
    }

    if (typeof params.includeRawSources === 'boolean') {
      paramsBuilder.set('includeRawSources', String(params.includeRawSources));
    }

    return `${UNIFIED_DIGEST_SEARCH_ROUTE}?${paramsBuilder.toString()}`;
  },
  getUnifiedDigestEmailPreferences: () =>
    UNIFIED_DIGEST_EMAIL_PREFERENCES_ROUTE,
  updateUnifiedDigestEmailPreferences: () =>
    UNIFIED_DIGEST_EMAIL_PREFERENCES_ROUTE,
  getPersonalActionRecapPreview: (timeframe: 'daily' | 'weekly') =>
    `${PERSONAL_ACTION_RECAP_PREVIEW_ROUTE}?timeframe=${encodeURIComponent(
      timeframe,
    )}`,
  getSuperAdminUnifiedDigestEmailPreferences: (targetUserId: string) =>
    `${UNIFIED_DIGEST_SUPER_ADMIN_EMAIL_PREFERENCES_ROUTE}/${encodeURIComponent(
      targetUserId,
    )}`,
  getSuperAdminUnifiedDigestEmailPreferencesByOrganization: (
    targetUserId: string,
    organizationId?: string,
  ) => {
    const baseRoute = `${UNIFIED_DIGEST_SUPER_ADMIN_EMAIL_PREFERENCES_ROUTE}/${encodeURIComponent(
      targetUserId,
    )}`;

    return organizationId
      ? `${baseRoute}?orgId=${encodeURIComponent(organizationId)}`
      : baseRoute;
  },
  getSuperAdminUnifiedDigestEmailPreferencesByOrganizationId: (
    organizationId: string,
    page: number,
    limit: number,
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    return `${UNIFIED_DIGEST_SUPER_ADMIN_EMAIL_PREFERENCES_ROUTE}/organizations/${encodeURIComponent(
      organizationId,
    )}?${params.toString()}`;
  },
  updateSuperAdminUnifiedDigestEmailPreferences: (
    targetUserId: string,
    organizationId?: string,
  ) => {
    const baseRoute = `${UNIFIED_DIGEST_SUPER_ADMIN_EMAIL_PREFERENCES_ROUTE}/${encodeURIComponent(
      targetUserId,
    )}`;

    return organizationId
      ? `${baseRoute}?orgId=${encodeURIComponent(organizationId)}`
      : baseRoute;
  },
  getReportGroupsByOrgIdAndUserId: () => `/report-group/organizations/users`,
  updateReportGroupsByOrgIdAndUserId: (groupId: string) =>
    `/report-group/organizations/users/groups/${groupId}`,
  getReportMessagesByGroupId: (groupId: string, page: number, limit: number) =>
    `/report-message/groups/${groupId}?page=${page}&limit=${limit}`,
  createReportMessage: () => `/report-message`,
  updateReportMessage: (messageId: string) => `/report-message/${messageId}`,
  deleteReportMessageById: (messageId: string) =>
    `/report-message/${messageId}`,
  createTextMemory: () => `/memory/text-memory`,
  createPdfMemory: () => `/memory/document/pdf-memory`,
  createDocDocxMemory: () => `/memory/document/doc-docx-memory`,
  createImageMemory: () => `/memory/image-memory`,
  createGoogleDocsMemory: () => `/memory/document/google-docs-memory`,
  createGoogleDriveMemory: () => `/memory/document/google-drive-memory`,
  googleSheetsAnalysis: (
    driveUrl: string,
    previewMaxRows: number,
    previewMaxColumns: number,
  ) =>
    `/memory/document/google-sheets-analysis?driveUrl=${encodeURIComponent(
      driveUrl,
    )}&previewMaxRows=${previewMaxRows}&previewMaxColumns=${previewMaxColumns}`,
  googleSheetsAnalysisAsSuperAdmin: (
    driveUrl: string,
    previewMaxRows: number,
    previewMaxColumns: number,
    userId: string,
  ) =>
    `/memory/super-admin/document/google-sheets-analysis/${userId}?driveUrl=${encodeURIComponent(
      driveUrl,
    )}&previewMaxRows=${previewMaxRows}&previewMaxColumns=${previewMaxColumns}`,
  updateSheetOptions: (chunkBatchId: string) =>
    `/memory/${chunkBatchId}/sheets/options`,
  updateSheetOptionsAsSuperAdmin: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/sheets/options/${chunkBatchId}/${userId}`,
  createRecursiveUrlMemory: () => `/memory/recursive-url-memory`,
  createConfluenceMemory: () => `/memory/document/confluence-memory`,
  createJiraMemory: () => `/memory/jira`,
  createGoogleChatSpaceMemory: () => `/memory/google-chat-space`,
  createGoogleChatSpaceAutoTracking: () =>
    `/memory/google-chat-space-auto-tracking`,
  createYoutubeCaptionsMemory: () => `/memory/youtube-captions-memory`,
  createGmailInboxLabelMemory: () => `/memory/gmail`,
  createGmailInboxLabelAutoTrackingMemory: () => `/memory/gmail/auto-tracking`,
  listGmailLabels: () => `/memory/gmail/labels`,
  createAudioMemory: () => `/memory/audio-memory`,
  createSuperAdminTextMemory: (userId: string) =>
    `/memory/super-admin/text-memory/${userId}`,
  createSuperAdminPdfMemory: (userId: string) =>
    `/memory/super-admin/document/pdf-memory/${userId}`,
  createSuperAdminDocDocxMemory: (userId: string) =>
    `/memory/super-admin/document/doc-docx-memory/${userId}`,
  createSuperAdminGoogleDocsMemory: (userId: string) =>
    `/memory/super-admin/document/google-docs-memory/${userId}`,
  createSuperAdminGoogleDriveMemory: (userId: string) =>
    `/memory/super-admin/document/google-drive-memory/${userId}`,
  createSuperAdminRecursiveUrlMemory: (userId: string) =>
    `/memory/super-admin/recursive-url-memory/${userId}`,
  createSuperAdminConfluenceMemory: (userId: string) =>
    `/memory/super-admin/document/confluence-memory/${userId}`,
  createSuperAdminJiraMemory: (userId: string) =>
    `/memory/super-admin/jira/${userId}`,
  createSuperAdminGoogleChatSpaceMemory: (userId: string) =>
    `/memory/super-admin/google-chat-space/${userId}`,
  createSuperAdminGoogleChatSpaceAutoTracking: (userId: string) =>
    `/memory/super-admin/google-chat-space-auto-tracking/${userId}`,
  createSuperAdminYoutubeCaptionsMemory: (userId: string) =>
    `/memory/super-admin/youtube-captions-memory/${userId}`,
  createSuperAdminAudioMemory: (userId: string) =>
    `/memory/super-admin/audio-memory/${userId}`,
  createSuperAdminImageMemory: (userId: string) =>
    `/memory/super-admin/image-memory/${userId}`,
  abortSuperAdminMemoryBatch: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/${chunkBatchId}/abort/${userId}`,
  deleteSuperAdminMemoryBatch: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/${chunkBatchId}/${userId}`,
  getAllMemoryBatchesForSuperAdmin: (
    page: number,
    limit: number,
    batchType?: MEMORY_BATCH_TYPES,
    searchQuery?: string | null,
    batchId?: string,
    status?: MEMORY_STATUS,
    ingestionStatus?: MEMORY_BATCH_INGESTION_STATUS,
    memoryLevel?: MEMORY_LEVELS,
    orgId?: string,
    tags?: string[],
  ) =>
    `/memory/super-admin/batches?page=${page}&limit=${limit}${
      batchType ? `&batchType=${batchType}` : ''
    }${status ? `&status=${status}` : ''}${
      ingestionStatus ? `&ingestionStatus=${ingestionStatus}` : ''
    }${memoryLevel ? `&memoryLevel=${memoryLevel}` : ''}${
      searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''
    }${batchId ? `&batchId=${encodeURIComponent(batchId)}` : ''}${
      orgId ? `&orgId=${encodeURIComponent(orgId)}` : ''
    }${
      tags && tags.length > 0
        ? `&tags=${encodeURIComponent(tags.join(','))}`
        : ''
    }`,
  getErroredMemoryBatchesForSuperAdmin: (
    page: number,
    limit: number,
    batchType?: MEMORY_BATCH_TYPES,
    searchQuery?: string | null,
    batchId?: string,
    status?: MEMORY_STATUS,
    memoryLevel?: MEMORY_LEVELS,
    orgId?: string,
    tags?: string[],
  ) =>
    `/memory/super-admin/batches/errored?page=${page}&limit=${limit}${
      batchType ? `&batchType=${batchType}` : ''
    }${status ? `&status=${status}` : ''}${
      memoryLevel ? `&memoryLevel=${memoryLevel}` : ''
    }${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}${
      batchId ? `&batchId=${encodeURIComponent(batchId)}` : ''
    }${orgId ? `&orgId=${encodeURIComponent(orgId)}` : ''}${
      tags && tags.length > 0
        ? `&tags=${encodeURIComponent(tags.join(','))}`
        : ''
    }`,
  getMemoryBatchList: (
    page: number,
    limit: number,
    batchType: MEMORY_BATCH_TYPES,
    status?: MEMORY_STATUS,
    memoryLevel?: MEMORY_LEVELS,
    includeGlobalMemories?: boolean,
    tags?: string[],
  ) =>
    `/memory/organizations?page=${page}&limit=${limit}&batchType=${batchType}${
      status ? `&status=${status}` : ''
    }${memoryLevel ? `&memoryLevel=${memoryLevel}` : ''}${
      includeGlobalMemories !== undefined
        ? `&includeGlobalMemories=${includeGlobalMemories}`
        : ''
    }${
      tags && tags.length > 0
        ? `&tags=${encodeURIComponent(tags.join(','))}`
        : ''
    }`,
  searchMemoryBatchList: (
    page: number,
    limit: number,
    batchType: MEMORY_BATCH_TYPES,
    searchQuery: string | null,
    status?: MEMORY_STATUS,
    memoryLevel?: MEMORY_LEVELS,
    includeGlobalMemories?: boolean,
    tags?: string[],
  ) => {
    return `/memory/search/organizations?page=${page}&limit=${limit}&batchType=${batchType}&search=${searchQuery}${
      status ? `&status=${status}` : ''
    }${memoryLevel ? `&memoryLevel=${memoryLevel}` : ''}${
      includeGlobalMemories !== undefined
        ? `&includeGlobalMemories=${includeGlobalMemories}`
        : ''
    }${
      tags && tags.length > 0
        ? `&tags=${encodeURIComponent(tags.join(','))}`
        : ''
    }`;
  },
  searchMemoryChunksWithBatch: (
    page: number,
    limit: number,
    searchQuery: string,
    batchType?: MEMORY_BATCH_TYPES,
    status?: MEMORY_STATUS,
    ingestionStatus?: MEMORY_BATCH_INGESTION_STATUS,
    memoryLevel?: MEMORY_LEVELS,
    includeGlobalMemories?: boolean,
    chunkBatchId?: string,
    tags?: string[],
  ) =>
    `/memory/search/chunks?page=${page}&limit=${limit}&search=${encodeURIComponent(
      searchQuery,
    )}${batchType ? `&batchType=${batchType}` : ''}${
      status ? `&status=${status}` : ''
    }${ingestionStatus ? `&ingestionStatus=${ingestionStatus}` : ''}${
      memoryLevel ? `&memoryLevel=${memoryLevel}` : ''
    }${includeGlobalMemories !== undefined ? `&includeGlobalMemories=${includeGlobalMemories}` : ''}${
      chunkBatchId ? `&chunkBatchId=${encodeURIComponent(chunkBatchId)}` : ''
    }${
      tags && tags.length > 0
        ? `&tags=${encodeURIComponent(tags.join(','))}`
        : ''
    }`,
  syncMemoryBatch: (chunkBatchId: string) => `/memory/sync/${chunkBatchId}`,
  enableAutoResync: (chunkBatchId: string) =>
    `/memory/resync/enable/${chunkBatchId}`,
  disableAutoResync: (chunkBatchId: string) =>
    `/memory/resync/disable/${chunkBatchId}`,
  syncSuperAdminMemoryBatch: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/sync/${chunkBatchId}/${userId}`,
  enableSuperAdminAutoResync: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/resync/enable/${chunkBatchId}/${userId}`,
  disableSuperAdminAutoResync: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/resync/disable/${chunkBatchId}/${userId}`,
  deleteMemoryBatch: (chunkBatchId: string) => `/memory/${chunkBatchId}`,
  getMemoryBatch: (chunkBatchId: string) => `/memory/${chunkBatchId}`,
  getStitchedMemoryBatch: (chunkBatchId: string) =>
    `/memory/stitched/${chunkBatchId}`,
  getSuperAdminMemoryBatch: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/${chunkBatchId}/${userId}`,
  getSuperAdminStitchedMemoryBatch: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/stitched/${chunkBatchId}/${userId}`,
  getIngestedMemoriesByBatchId: (
    chunkBatchId: string,
    page: number,
    limit: number,
  ) => `/memory/ingested/${chunkBatchId}?page=${page}&limit=${limit}`,
  getSuperAdminIngestedMemoriesByBatchId: (
    chunkBatchId: string,
    page: number,
    limit: number,
    userId: string,
  ) =>
    `/memory/super-admin/ingested/${chunkBatchId}/${userId}?page=${page}&limit=${limit}`,
  getMemoryPageDataByBatchId: (
    chunkBatchId: string,
    page: number,
    limit: number,
  ) => `/memory/page-data/${chunkBatchId}?page=${page}&limit=${limit}`,
  getSuperAdminMemoryPageDataByBatchId: (
    chunkBatchId: string,
    page: number,
    limit: number,
    userId: string,
  ) =>
    `/memory/super-admin/page-data/${chunkBatchId}/${userId}?page=${page}&limit=${limit}`,
  updateMemoryBatch: (chunkBatchId: string) => `/memory/${chunkBatchId}`,
  updateMemoryById: (memoryId: string) => `/memory/single/${memoryId}`,
  abortMemoryBatch: (chunkBatchId: string) => `/memory/${chunkBatchId}/abort`,
  retryMemoryBatchIngestion: (chunkBatchId: string) =>
    `/memory/retry/${chunkBatchId}`,
  retrySuperAdminMemoryBatchIngestion: (chunkBatchId: string, userId: string) =>
    `/memory/super-admin/retry/${chunkBatchId}/${userId}`,
  getTotalActiveSpacesIntegrated: () =>
    `/spaces/organizations/spaces-count?isActive=true`,
  getTotalUsersAuthorized: () => `/users/organizations/users-count`,
  getOrganizationInteractionCountForMonth: (currentDate: string) =>
    `/usage/organizations/interaction-count?currentDate=${currentDate}`,
  getUserInteractionCountForMonth: (currentDate: string) =>
    `/usage/organizations/users/interaction-count?currentDate=${currentDate}`,
  getAllUsers: (
    page: number,
    limit: number,
    search: string | undefined = undefined,
  ) =>
    `/users/organizations?page=${page}&limit=${limit}${
      search ? `&search=${search}` : ''
    }`,
  updateUserActiveStatus: () => `/users/update-active-status`,
  updateUserRole: () => `/users/update-role`,
  getUserInteractionDataForSelectedDates: (
    startDate: string,
    endDate: string,
    timeZoneOffset: string,
  ) =>
    `/usage/organizations/users/interaction-data-by-dates?startDate=${startDate}&endDate=${endDate}&timeZoneOffset=${timeZoneOffset}`,
  getOrgInteractionDataForSelectedDates: (
    startDate: string,
    endDate: string,
    timeZoneOffset: string,
  ) =>
    `/usage/organizations/interaction-data-by-dates?startDate=${startDate}&endDate=${endDate}&timeZoneOffset=${timeZoneOffset}`,
  updateOrganization: () => `/organization`,
  createOrganization: () => `/organization`,
  saveOnboardingDetails: () => `/organization/onboarding`,
  getSupportedTimezones: () => `/organization/timezones`,
  getOrganization: () => `/organization/active-organization`,
  getOrganizationsForDomainDirectory: () => `/organization/domain-directory`,
  lookupOrganizationsForDomainDirectoryByEmail: () =>
    `/organization/domain-directory/lookup`,
  getUsersForCorporateDomainDirectory: () =>
    `/organization/domain-directory/people`,
  createOrganizationJoinRequest: () => `/organization/join-requests`,
  listOrganizationJoinRequests: (
    status?: string,
    page?: number,
    limit?: number,
  ) => {
    const params = new URLSearchParams();

    if (status) {
      params.set('status', status);
    }

    if (typeof page === 'number') {
      params.set('page', String(page));
    }

    if (typeof limit === 'number') {
      params.set('limit', String(limit));
    }

    const queryString = params.toString();

    return queryString
      ? `/organization/join-requests?${queryString}`
      : `/organization/join-requests`;
  },
  handleOrganizationJoinRequestDecision: (requestId: string) =>
    `/organization/join-requests/${requestId}`,
  getAllOrganizationsUserIsMemberOf: (page: number, limit: number) =>
    `/organization?page=${page}&per_page=${limit}`,
  checkDnsVerification: () => `/organization/check-dns-verification`,
  checkOnboardingComplete: () => `/users/check-onboarding-complete`,
  completeRevampOnboarding: () => `/organization/onboarding/revamp-complete`,
  updateAdultConsent: () => `/users/adult-consent`,
  getEmailPreferencesLink: () => `/users/email-preferences-link`,
  updateUserTimezone: () => `/users/timezone`,
  inviteUsersToOrgViaEmail: () => `/organization/invite-by-email`,
  getInvitedUsersByOrgId: (
    page: number,
    limit: number,
    search: string | undefined = undefined,
  ) =>
    `/organization/invited-users?page=${page}&limit=${limit}${
      search ? `&search=${search}` : ''
    }`,
  changeActiveOrganization: () => `/users/change-active-organization`,
  joinOrganizationWithInviteToken: (inviteToken: string) =>
    `/users/accept-invitation?inviteToken=${inviteToken}`,
  updateResponseControlCardState: () => `/organization/response-control-card`,
  updateResponseMetadataState: () => `/organization/response-metadata`,
  updatePrivateChatState: () => `/organization/private-chat`,
  updateInvocationHistoryState: () => `/organization/invocation-history`,
  getRevampSettings: () => `/organization/revamp-settings`,
  updateRevampSettings: () => `/organization/revamp-settings`,
  getHanaInstructions: () => `/organization/hana-instructions`,
  updateHanaInstructions: () => `/organization/hana-instructions`,
  updateInvocationHistoryStateForOrg: (orgId: string) =>
    `/organization/${orgId}/invocation-history`,
  createDnsVerificationCode: () => `/dns-verification`,
  findDnsVerificationDetails: () => `/dns-verification/organizations/users`,
  verifyDnsByOrgIdAndUserId: () => `/dns-verification/verify`,
  getOrganizationSpaceData: (page: number, limit: number) =>
    `/spaces/organizations/?page=${page}&limit=${limit}`,
  getOrganizationSpaceLookupData: (
    page: number,
    limit: number,
    search?: string,
  ) =>
    `/spaces/organizations/lookup?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  getOrganizationSpaceLookupDataAsSuperAdmin: (
    userId: string,
    page: number,
    limit: number,
    search?: string,
  ) =>
    `/spaces/super-admin/lookup/${userId}?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  getOrganizationMemorySpaceLookupData: (
    page: number,
    limit: number,
    search?: string,
  ) =>
    `/spaces/organizations/memory-lookup?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  getOrganizationMemorySpaceLookupDataAsSuperAdmin: (
    userId: string,
    page: number,
    limit: number,
    search?: string,
  ) =>
    `/spaces/super-admin/memory-lookup/${userId}?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  getActivationGetStarted: (includeWorkspaceState = false) =>
    `/activation/get-started${
      includeWorkspaceState ? '?includeWorkspaceState=true' : ''
    }`,
  getActivationRecommendations: () => `/activation/recommendations`,
  trackActivationEvent: () => `/activation/track`,
  updateSpaceDetailsById: (spaceId: string) => `spaces/${spaceId}`,
  getOrganizationSpaceDataAsSuperAdmin: (
    userId: string,
    page: number,
    limit: number,
    organizationId?: string,
    includeAllVisibleSpaces?: boolean,
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (organizationId) {
      params.set('orgId', organizationId);
    }

    if (includeAllVisibleSpaces) {
      params.set('includeAllVisibleSpaces', 'true');
    }

    return `/spaces/super-admin/organizations/${userId}?${params.toString()}`;
  },
  getSpaceOpenItemsRecapPreference: (spaceId: string) =>
    `/spaces/${spaceId}/automations/end-of-day-open-items-recap`,
  updateSpaceOpenItemsRecapPreference: (spaceId: string) =>
    `/spaces/${spaceId}/automations/end-of-day-open-items-recap`,
  getSpaceOpenItemsRecapRuns: (spaceId: string) =>
    `/spaces/${spaceId}/automations/end-of-day-open-items-recap/runs`,
  triggerSpaceOpenItemsRecapTestRun: (spaceId: string) =>
    `/spaces/${spaceId}/automations/end-of-day-open-items-recap/test-run`,
  getDailyRecapGeneralInstructionsSettings: () =>
    `/spaces/automations/end-of-day-open-items-recap/general-instructions`,
  getSpaceAutomationOverview: (page: number, limit: number) =>
    `/spaces/automations/overview?page=${page}&limit=${limit}`,
  getTeamFollowThroughOverview: (
    page: number,
    limit: number,
    query?: string,
    status?: string,
  ) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      status: status || 'all',
    });

    if (query) params.set('query', query);

    return `/spaces/automations/team-follow-through?${params.toString()}`;
  },
  updateTeamFollowThroughPreference: () =>
    '/spaces/automations/team-follow-through',
  updateDailyRecapGeneralInstructionsSettings: () =>
    `/spaces/automations/end-of-day-open-items-recap/general-instructions`,
  getSuperAdminDailyRecapGeneralInstructionsSettings: (
    targetUserId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/automations/end-of-day-open-items-recap/general-instructions${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  updateSuperAdminDailyRecapGeneralInstructionsSettings: (
    targetUserId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/automations/end-of-day-open-items-recap/general-instructions${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  getSuperAdminSpaceOpenItemsRecapPreference: (
    targetUserId: string,
    spaceId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/${spaceId}/automations/end-of-day-open-items-recap${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  updateSuperAdminSpaceOpenItemsRecapPreference: (
    targetUserId: string,
    spaceId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/${spaceId}/automations/end-of-day-open-items-recap${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  getSuperAdminSpaceOpenItemsRecapRuns: (
    targetUserId: string,
    spaceId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/${spaceId}/automations/end-of-day-open-items-recap/runs${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  triggerSuperAdminSpaceOpenItemsRecapTestRun: (
    targetUserId: string,
    spaceId: string,
    organizationId?: string,
  ) =>
    `/spaces/super-admin/${targetUserId}/${spaceId}/automations/end-of-day-open-items-recap/test-run${
      organizationId ? `?orgId=${encodeURIComponent(organizationId)}` : ''
    }`,
  getActiveOrganizationDetails: () => `/organization/active-organization`,
  getPricingPlansDetails: (planName?: string) =>
    `/plans/client-pricing-details?${planName ? 'plan=' + planName : ''}`,
  getAllLatestPolicies: () => `/policy/get-all-latest-policies`,
  createUserConsentPolicy: () => `/user-consent-policy`,
  checkLatestPoliciesApprovedByMongoUserId: () =>
    `/user-consent-policy/check-latest-policies-approval`,
  getLatestPoliciesNotApprovedByMongoUserId: () =>
    `/user-consent-policy/not-approved-policies`,
  deleteOrganizationById: (orgId: string) => `/delete/organizations/${orgId}`,
  deleteUsers: () => `/delete/users`,
  getEmailOutreachCampaigns: () => `/email-outreach/campaigns`,
  toggleEmailOutreachCampaign: (campaignKey: string) =>
    `/email-outreach/campaigns/${campaignKey}/toggle`,
  updateEmailOutreachCampaignConfig: (campaignKey: string) =>
    `/email-outreach/campaigns/${campaignKey}/config`,
  createEmailOutreachJobs: () => `/email-outreach/on-demand`,
  scheduleEmailOutreachBlast: () => `/email-outreach/blast`,
  getEmailOutreachJobs: (params?: {
    page?: number;
    limit?: number;
    campaignKey?: string;
    status?: string;
  }) => {
    const query = new URLSearchParams();

    if (typeof params?.page === 'number') {
      query.set('page', String(params.page));
    }

    if (typeof params?.limit === 'number') {
      query.set('limit', String(params.limit));
    }

    if (params?.campaignKey) {
      query.set('campaignKey', params.campaignKey);
    }

    if (params?.status) {
      query.set('status', params.status);
    }

    const queryString = query.toString();

    return queryString
      ? `/email-outreach/jobs?${queryString}`
      : `/email-outreach/jobs`;
  },
  cancelEmailOutreachJob: (jobId: string) =>
    `/email-outreach/jobs/${jobId}/cancel`,
  createRealtimeSession: () => `realtime-conversation/create-session`,
  createCompletionForStandaloneQuestion: () =>
    `realtime-conversation/create-completion-for-realtime`,
  createCompletionAsUser: (userId: string) =>
    `/expose-api/super-admin/chat/completion/${userId}`,
  getAsyncCompletionsStatusForUser: (
    userId: string,
    messageId?: string,
    page?: number,
    limit?: number,
  ) =>
    `/expose-api/super-admin/chat/statuses/${userId}?${
      messageId ? `messageId=${messageId}&` : ''
    }${page ? `page=${page}&` : ''}${limit ? `per_page=${limit}` : ''}`,
  validateMongoUserId: (userId: string) => `/users/${userId}/validate`,
  validateOrganizationId: (orgId: string) => `/organization/${orgId}/validate`,
  scheduleDmAnnouncement: () => `crm/users/all/schedule-dm-announcement`,
  getScheduledDmAnnouncementsBySuperAdminOrgId: (page: number, limit: number) =>
    `crm/scheduled-dm-announcements/organizations?page=${page}&limit=${limit}`,
  deleteDmAnnouncementByJobId: (jobId: string) =>
    `crm/dm-announcement/${jobId}`,
  abortDmAnnouncementByJobId: (jobId: string) =>
    `crm/dm-announcement/${jobId}/abort`,
  connectLemonsqueezyEventSource: () => `/lemonsqueezy/events/subscribe`,
  checkLemonsqueezyExistingSubscriptionFromDb: () =>
    `/lemonsqueezy/subscriptions/organizations/check-lemonsqueezy-existing-subscriptions`,
  createCheckoutInLemonsqueezy: () => `lemonsqueezy/checkout`,
  getLemonsqueezySubscriptionInterval: () =>
    `lemonsqueezy/subscriptions/organizations/recurring-interval`,
  updateSubscriptionInLemonsqueezy: () =>
    `lemonsqueezy/subscriptions/organizations`,
  cancelSubscriptionInLemonsqueezy: () =>
    `lemonsqueezy/subscriptions/organizations/cancel`,
  resumeSubscriptionInLemonsqueezy: () =>
    `lemonsqueezy/subscriptions/organizations/resume`,
  getSubscriptionStatusInLemonsqueezy: () =>
    `lemonsqueezy/subscriptions/organizations/status`,
  getSubscriptionInvoices: (page: number, limit: number) =>
    `lemonsqueezy/subscriptions/organizations/invoices?page=${page}&limit=${limit}`,
  customEventTracking: () => `custom-event-tracking/api/v1/track-event`,
  getReportMessageById: (messageId: string) => `report-message/${messageId}`,
  testDmAnnouncement: () => `crm/dm-announcement/test`,
  getCrmBlockedUsers: (page: number, limit: number, search?: string) =>
    `crm/blocked-users?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  blockCrmUser: () => `crm/blocked-users`,
  unblockCrmUser: (blockedUserId: string) =>
    `crm/blocked-users/${blockedUserId}/unblock`,
  sendUpgradeRequest: () => `/agenda-jobs/upgrade-request`,
  contactSupport: () => `/contact-support`,
  getSlashCommandsCount: (startDate: string, endDate: string) =>
    `usage/organizations/users/slash-commands-usage?startDate=${startDate}&endDate=${endDate}`,
  getMemoriesCount: () => `/memory/organizations/memories-count`,
  getTasksCount: () => `/task/organizations/tasks-count`,
  getMostActiveUsers: (startDate: string, endDate: string, limit: number) =>
    `/usage/organizations/top-users?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
  getMostActiveSpaces: (startDate: string, endDate: string, limit: number) =>
    `/usage/organizations/top-spaces?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
  getSignUpUrl: (redirect?: string) =>
    `/oauth/signup-url?authFor=${AUTH_FOR.FRONT_END}${redirect ? '&redirect=' + encodeURIComponent(redirect) : ''}`,
  getOauthReauthSignUpUrl: (intentToken: string) =>
    `/oauth/reauth-signup-url?intent=${encodeURIComponent(intentToken)}`,
  resolveOrganizationInvitationCode: () =>
    `/organization/invitation-code/resolve`,
  getAuthUrlForAdditionalScopes: () => `/oauth/incremental-scopes-consent`,
  getOauthScopesOverview: () => `/oauth/scopes`,
  getOauthScopesSignInUrl: () => `/oauth/scopes/signin-url`,
  getMaintenanceStatus: () => `/maintenance-status`,
  createRedirectProxy: () => `/redirect-proxy`,
  getAllRedirectProxy: (page: number, limit: number) =>
    `/redirect-proxy/all?page=${page}&limit=${limit}`,
  updateRedirectProxyById: (redirectProxyId: string) =>
    `/redirect-proxy/${redirectProxyId}`,
  deleteRedirectProxyById: (redirectProxyId: string) =>
    `/redirect-proxy/${redirectProxyId}`,
  createFastApiProxy: () => `/fast-api-proxy`,
  getAllFastApiProxy: (page: number, limit: number) =>
    `/fast-api-proxy/all?page=${page}&limit=${limit}`,
  updateFastApiProxyById: (fastApiProxyId: string) =>
    `/fast-api-proxy/${fastApiProxyId}`,
  deleteFastApiProxyById: (fastApiProxyId: string) =>
    `/fast-api-proxy/${fastApiProxyId}`,
  getRundownUpdateDetails: (type: keyof typeof RUNDOWN_UPDATE_TYPE) =>
    `/rundown-update/details?type=${type}`,
  handleUpdateRundownUpdate: (rundownUpdateId: string) =>
    `/rundown-update/${rundownUpdateId}`,
  deactivateAccount: () => `/account/deactivate`,
  confirmOrganizationDeletion: () => `/account/deletion`,
  checkIfAccountDeactivated: () => `/account/deactivate/status`,
  reactivateAccount: () => `/account/reactivate`,
  getSupportedLanguages: () => `organization/languages`,
  getConnectedConnectorsInOrg: (variant?: string) =>
    `connector/connected/all${variant ? `?variant=${variant}` : ''}`,
  getConnectedConnectorsByType: (
    connectorType: string,
    page: number,
    limit: number,
    searchQuery?: string,
    variant?: string,
  ) =>
    `connector/connected/search?connectorType=${connectorType}&page=${page}&limit=${limit}${searchQuery ? `&search=${searchQuery}` : ''}${variant ? `&variant=${variant}` : ''}`,
  connectConnector: () => `connector/connect`,
  disconnectConnector: () => `connector/disconnect`,
  discoverMcpTools: (connectedConnectorId: string) =>
    `connector/mcp/${connectedConnectorId}/discover-tools`,
  getMcpTools: (connectedConnectorId: string) =>
    `connector/mcp/${connectedConnectorId}/tools`,
  getMcpOauthProviders: () => `connector/mcp/oauth/providers`,
  startMcpOauth: () => `connector/mcp/oauth/start`,
  refreshMcpAuth: (connectedConnectorId: string) =>
    `connector/mcp/${connectedConnectorId}/refresh-auth`,
  getMcpAuthStatus: (connectedConnectorId: string) =>
    `connector/mcp/${connectedConnectorId}/auth-status`,
  generateUniqueApiKey: () => `connector/unique-api-key`,
  getConnectorById: (connectorId: string) => `connector/${connectorId}`,
  getAsanaConnectorSignInUrl: () =>
    `asana/signin-url?authFor=${AUTH_FOR.FRONT_END}`,
  deleteAllAutoMemoryIngestion: () => `/brain-memory/organization`,
  deleteUserAllAutoMemoryIngestion: (userId: string) =>
    `/brain-memory/user/${userId}`,
  promoteAdmin: () => `crm/promote-admin`,
  superAdminSearchUsers: (params: {
    nameOrEmail?: string;
    userId?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();

    if (params.nameOrEmail) {
      queryParams.set('nameOrEmail', params.nameOrEmail);
    }

    if (params.userId) {
      queryParams.set('userId', params.userId);
    }

    if (typeof params.limit === 'number') {
      queryParams.set('limit', String(params.limit));
    }

    const queryString = queryParams.toString();

    return queryString
      ? `crm/super-admin/users/search?${queryString}`
      : `crm/super-admin/users/search`;
  },
  superAdminSearchOrganizations: (params: {
    nameOrWebsite?: string;
    organizationId?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();

    if (params.nameOrWebsite) {
      queryParams.set('nameOrWebsite', params.nameOrWebsite);
    }

    if (params.organizationId) {
      queryParams.set('organizationId', params.organizationId);
    }

    if (typeof params.limit === 'number') {
      queryParams.set('limit', String(params.limit));
    }

    const queryString = queryParams.toString();

    return queryString
      ? `crm/super-admin/organizations/search?${queryString}`
      : `crm/super-admin/organizations/search`;
  },
  superAdminSearchMemoryBatches: (batchId: string) => {
    const queryParams = new URLSearchParams();

    queryParams.set('batchId', batchId);

    return `crm/super-admin/memory-batches/search?${queryParams.toString()}`;
  },
  getCrmLeads: (queryString?: string) =>
    `crm/leads${queryString ? queryString : ''}`,
  backfillCrmLeads: (queryString?: string) =>
    `crm/leads/backfill${queryString ? queryString : ''}`,
  getCrmLeadKpis: () => `crm/leads/kpis/summary`,
  getCrmLeadOutreachSettings: () => `crm/leads/outreach/settings`,
  getCrmLeadById: (leadId: string) => `crm/leads/${leadId}`,
  updateCrmLeadOutreachSettings: () => `crm/leads/outreach/settings`,
  updateCrmLeadStatus: (leadId: string) => `crm/leads/${leadId}`,
  addCrmLeadNote: (leadId: string) => `crm/leads/${leadId}/notes`,
  triggerCrmLeadGenerateOutreach: (leadId: string) =>
    `crm/leads/${leadId}/generate-outreach`,
  offerCrmLeadReactivationTrial: (leadId: string) =>
    `crm/leads/${leadId}/reactivation-trial`,
  deduplicateMemories: (dryRun: boolean) =>
    `/memory/super-admin/maintenance/deduplicate-memories?dryRun=${dryRun}`,
  cleanupOrphanedMemories: (dryRun: boolean) =>
    `/memory/super-admin/maintenance/cleanup-orphaned-memories?dryRun=${dryRun}`,
  runBirthdayTest: (userId: string) =>
    `/test-utils/super-admin/birthday/${userId}`,
  getInvocationEvalCatalog: () => `/test-utils/super-admin/evals/catalog`,
  runInvocationEvalSuite: () => `/test-utils/super-admin/evals/run`,
  runInvocationReplayCase: (caseId: string) =>
    `/test-utils/super-admin/evals/replay/${caseId}`,
  runInvocationShadowEval: () => `/test-utils/super-admin/evals/shadow`,
  bulkAddUsersToOrgByEmail: (orgId: string) =>
    `/organization/${orgId}/bulk-add-users`,
  bulkUpdateUsersActiveOrgByEmail: (orgId: string) =>
    `/organization/${orgId}/bulk-update-users`,
  countBrainMemories: (level: BrainMemoryCountLevel) =>
    `/brain-memory/count?level=${level}`,
  getOrgGraphBrainMemories: () => `/brain-memory/organization/graph`,
  getOrgSavedPrompts: () => `/saved-prompts`,
  updateSavedPromptById: (promptId: string) => `/saved-prompts/${promptId}`,
  getSavedPromptById: (promptId: string) => `/saved-prompts/${promptId}`,
  createSavedPrompt: () => `/saved-prompts`,
  deleteSavedPromptById: (promptId: string) => `/saved-prompts/${promptId}`,
  createFilePick: () => '/file-picks/add-file-pick',
  getFilePicks: () => '/file-picks/fetch-file-picks',
  getPolls: (
    page: number,
    limit: number,
    search?: string,
    status?: string,
    isAnonymous?: boolean,
  ) =>
    `/polls?page=${page}&per_page=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }${status ? `&status=${encodeURIComponent(status)}` : ''}${
      typeof isAnonymous === 'boolean' ? `&isAnonymous=${isAnonymous}` : ''
    }`,
  getPollDashboardDetail: (pollId: string) => `/polls/${pollId}/dashboard`,
  updatePoll: (pollId: string) => `/polls/${pollId}`,
  deletePoll: (pollId: string) => `/polls/${pollId}`,
  revokeFilePick: (chunkBatchId: string) =>
    `memory/google-drive-memory/${chunkBatchId}/revoke`,
  updateFilePickStatus: (chunkBatchId: string) =>
    `memory/google-drive-memory/${chunkBatchId}/status`,
  fetchTasks: () => `/task/organizations`,
  fetchTaskDetails: (taskId: string) => `task/${taskId}`,
  createTaskUpdate: (taskId: string) => `task/${taskId}/updates`,
  getTaskUpdates: (taskId: string, page: number, limit: number) =>
    `task/${taskId}/updates?page=${page}&limit=${limit}`,
  listTaskReminders: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    status?: string;
  }) => {
    const query = new URLSearchParams();

    if (params?.page) {
      query.set('page', String(params.page));
    }

    if (params?.limit) {
      query.set('limit', String(params.limit));
    }

    if (params?.type) {
      query.set('type', params.type);
    }

    if (params?.status) {
      query.set('status', params.status);
    }

    const queryString = query.toString();

    return queryString ? `/task/reminders?${queryString}` : '/task/reminders';
  },
  getTaskReminderRecurringEligibility: () =>
    '/task/reminders/recurring/eligibility',
  getTaskReminder: (reminderId: string) => `/task/reminders/${reminderId}`,
  createTaskReminder: () => '/task/reminders',
  upsertTaskReminderForTask: (taskId: string) => `/task/${taskId}/reminder`,
  updateTaskReminder: (reminderId: string) => `/task/reminders/${reminderId}`,
  deleteTaskReminder: (reminderId: string) => `/task/reminders/${reminderId}`,
  pauseTaskReminder: (reminderId: string) =>
    `/task/reminders/${reminderId}/pause`,
  resumeTaskReminder: (reminderId: string) =>
    `/task/reminders/${reminderId}/resume`,
  getSprints: (page: number, limit: number) =>
    `${SPRINT_BASE_ROUTE}/organizations?page=${page}&limit=${limit}`,
  getSprintCounts: () => `${SPRINT_BASE_ROUTE}/organizations/sprints-count`,
  createSprint: () => `${SPRINT_BASE_ROUTE}/organizations`,
  updateSprintById: (sprintId: string) => `${SPRINT_BASE_ROUTE}/${sprintId}`,
  updateSprintAutoManage: (sprintId: string) =>
    `${SPRINT_BASE_ROUTE}/${sprintId}/auto-manage`,
  completeSprintById: (sprintId: string) =>
    `/sprint/${encodeURIComponent(sprintId)}/complete`,
  updateSprintStatusById: (sprintId: string) =>
    `${SPRINT_BASE_ROUTE}/${sprintId}/status`,
  deleteSprintById: (sprintId: string) => `${SPRINT_BASE_ROUTE}/${sprintId}`,
  getSprintBoard: (sprintId: string, page: number, limit: number) =>
    `${SPRINT_BASE_ROUTE}/${sprintId}/board?page=${page}&limit=${limit}`,
  getSprintBacklog: () => `${SPRINT_BASE_ROUTE}/organizations/backlog`,
  assignSprintTasks: (sprintId: string) =>
    `${SPRINT_BASE_ROUTE}/${sprintId}/tasks/assign`,
  removeSprintTasks: (sprintId: string) =>
    `${SPRINT_BASE_ROUTE}/${sprintId}/tasks/remove`,
  getActiveSprintSummary: () =>
    `${SPRINT_BASE_ROUTE}/organizations/active-sprint/summary`,
  getAchievementStatuses: (tzOffset: string) =>
    `/achievement-engine/organizations/users?tzOffset=${tzOffset}`,
  // Tag CRUD endpoints
  createTag: () => `/tag`,
  getTags: (page: number, limit: number, search: string | null = null) =>
    `/tag/current-organization?page=${page}&limit=${limit}${
      search ? `&search=${encodeURIComponent(search)}` : ''
    }`,
  updateTagById: (tagId: string) => `/tag/${tagId}`,
  deleteTagById: (tagId: string) => `/tag/${tagId}`,
  createReview: () => `/reviews/rating`,
  getLatestReview: () => `/reviews/rating`,
  pollActiveUser: () => `/active-users/poll`,
  getActiveUsers: () => `/active-users`,
  getHighValueClientSnapshots: (queryString: string) =>
    `/high-value-clients${queryString}`,
  getHighValueClientSnapshotByOrganizationId: (organizationId: string) =>
    `/high-value-clients/${organizationId}`,
  refreshHighValueClientSnapshots: () => `/tasks/high-value-client-tracker`,
  getManualVerifications: () => `/manual-verifications`,
  getManualVerificationById: (verificationId: string) =>
    `/manual-verifications/${verificationId}`,
  acceptManualVerification: (verificationId: string) =>
    `/manual-verifications/${verificationId}/accept`,
  denyManualVerification: (verificationId: string) =>
    `/manual-verifications/${verificationId}/deny`,
  getAmlVerifications: () => `/v1/loket/admin/aml-verifications`,
  getAmlVerificationByProfileId: (profileId: string) =>
    `/v1/loket/admin/aml-verifications/${encodeURIComponent(profileId)}`,
  approveAmlVerificationByProfileId: (profileId: string) =>
    `/v1/loket/admin/aml-verifications/${encodeURIComponent(profileId)}/approve`,
  rejectAmlVerificationByProfileId: (profileId: string) =>
    `/v1/loket/admin/aml-verifications/${encodeURIComponent(profileId)}/reject`,
};
